"use client";

import type { AgentThreadShareVo } from "@/api";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { ModalHeader } from "@/component/ModalHeader";
import { useToast } from "@/component/Toast/useToast";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { fetchSharedThread } from "@/service/threadShare";
import { type UrlAttachment } from "@/types/attachment";
import { type ChatDraft } from "@/types/draft-strorage";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { attachmentsToPersisted } from "@/util/draftConverter";
import { getDraftKey, setDraft } from "@/util/draftStorage";
import "@ant-design/v5-patch-for-react-19";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { MAX_UPLOAD_FILE_COUNT } from "../../common/const";
import { ThreadMedia } from "./components/ThreadMedia";
import { ThreadMediaSkeleton } from "./components/ThreadMediaSkeleton";
import { ThreadSidebar } from "./components/ThreadSidebar";
import { ThreadSidebarSkeleton } from "./components/ThreadSidebarSkeleton";
import styles from "./index.module.css";
interface ThreadShareProps {
  id?: string;
  thread?: AgentThreadShareVo | null;
  onClose?: () => void;
}

const HOME_ROUTE = "/";

export function ThreadShare(props: ThreadShareProps) {
  const { id, thread: initialThread, onClose } = props;
  const router = useRouter();
  const { error: showErrorToast } = useToast();
  const { isLg, isXl, is2xl } = useBreakpoints();
  const isLargeScreen = isLg || isXl || is2xl;
  const [videoPlayerHeight, setVideoPlayerHeight] = useState<number>();

  const {
    data: fetchedThread,
    error,
    isLoading,
  } = useSWR(
    id && !initialThread ? ["shared-thread", id] : null,
    async ([, threadId]) => fetchSharedThread(threadId as string)
  );

  const thread = useMemo(
    () => initialThread || fetchedThread,
    [initialThread, fetchedThread]
  );

  const handleDialogClose = useCallback(async () => {
    if (onClose) {
      onClose();
      return;
    }
    await router.push(HOME_ROUTE);
  }, [router, onClose]);

  useEffect(() => {
    // Only auto-close if we explicitly have no thread and no loading/error state
    // If there's an error, we want to show it, not close the component.
    if (!thread && !isLoading && !error && (id || initialThread)) {
      console.error("ThreadShare: thread data is missing");
      handleDialogClose();
    }
  }, [thread, isLoading, error, id, initialThread, handleDialogClose]);

  const handleRemix = async () => {
    if (!thread) return;
    try {
      const attachments: UrlAttachment[] = (thread.files || [])
        .slice(0, MAX_UPLOAD_FILE_COUNT)
        .filter((file) => file.filePath)
        .map((file, index) => {
          const url = convertFilePathToUrl(file.filePath || "");
          return {
            type: "url" as const,
            id: `remix-${index}-${Date.now()}`,
            url,
            preview: url,
          };
        })
        .filter((attachment) => attachment.url);

      const persistedAttachments = await attachmentsToPersisted(attachments);

      const draft: ChatDraft = {
        text: thread.prompt || "",
        isAuto: true,
        attachments: persistedAttachments,
        updatedAt: Date.now(),
      };

      await setDraft(getDraftKey(), draft);

      window.open(HOME_ROUTE, "_blank");

      logger.event(VidMuseEvent.ThreadSharePageRemix, {
        thread_id: thread.id,
      });
    } catch (error) {
      console.error("Failed to remix thread:", error);
      showErrorToast("Failed to remix project. Please try again.");
    }
  };

  const handleOpenProject = useCallback(() => {
    if (!thread?.id) return;

    logger.event(VidMuseEvent.ThreadSharePageOpenProject, {
      thread_id: thread.id,
    });

    window.open(`/thread/${thread.id}`, "_blank");
  }, [thread?.id]);

  // 处理 VideoPlayer 高度变化的回调
  const handleVideoPlayerHeightChange = useCallback((height: number) => {
    setVideoPlayerHeight(height);
  }, []);

  // 断点变化时重置高度
  useEffect(() => {
    if (!isLargeScreen) {
      setVideoPlayerHeight(undefined);
    }
  }, [isLargeScreen]);

  return (
    <div className={styles.page}>
      <ModalHeader size="large" onClose={handleDialogClose} />
      <div className="w-full lg:h-[calc(100vh-96px)]">
        <div className="w-full h-full mx-auto flex flex-col gap-5 p-5 lg:pb-20 lg:max-w-[1600px] lg:flex-row">
          {isLoading ? (
            <ThreadMediaSkeleton />
          ) : error ? (
            <div className="flex-1 h-full flex items-center justify-center text-text-secondary bg-background-card rounded-2xl min-h-[300px]">
              <div className="text-center">
                <p className="text-lg font-medium">Failed to load thread</p>
                <p className="text-sm mt-2">
                  Please check the link or try again later.
                </p>
              </div>
            </div>
          ) : (
            <ThreadMedia
              src={thread?.resultPath}
              onVideoPlayerHeightChange={
                isLargeScreen ? handleVideoPlayerHeightChange : undefined
              }
            />
          )}

          {isLoading ? (
            <ThreadSidebarSkeleton />
          ) : error ? null : thread ? (
            <ThreadSidebar
              thread={thread}
              onRemix={handleRemix}
              onOpenProject={handleOpenProject}
              mediaHeight={videoPlayerHeight}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
